import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendSignInLinkToEmail,
  updateProfile,
} from 'firebase/auth'
import { SingUpWithGoogle } from '../../utils/FirebaseUtils.ts'
import {
  setFirebaseUser,
  setNeedsRegistration,
} from '../../features/FirebaseAuthSlice.ts'
import { COMPLETE_SIGN_UP_PATH } from '../../utils/SignUpDraft.ts'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'

/**
 * Reusable Firebase authentication step shared by the register-club flow and the
 * standalone sign-up flow. It only materializes the Firebase identity; the parent
 * decides what happens afterwards via the callbacks below.
 *
 * - Google and email/password are synchronous: on success `onAuthenticated` is
 *   called and the parent can immediately run its post-auth work (e.g. PUT /user
 *   then POST /clubs).
 * - Passwordless sends an email link that navigates away, so the parent persists
 *   whatever it needs in `onBeforePasswordless` and the completion route picks the
 *   draft back up.
 *
 * The component flips `needsRegistration` on before any synchronous auth so the
 * app doesn't fire a premature GET /user (which would 404 while the account is
 * still being provisioned). The parent is responsible for flipping it back off
 * once provisioning settles.
 */
export interface AuthStepProps {
  // Runs after a synchronous (Google / email+password) sign-in succeeds.
  onAuthenticated: () => void
  // Validate parent-owned state (e.g. the club form) before authenticating.
  // Return false to abort without touching Firebase.
  onBeforeAuth?: () => boolean
  // Persist any draft needed to resume after the passwordless email round-trip.
  // Return false to abort sending the link.
  onBeforePasswordless?: (email: string, displayName: string) => boolean
  // Tail of the "check your email" confirmation, letting each flow describe what
  // the link finishes (e.g. creating a club vs. signing up).
  passwordlessSentMessage?: string
}

export default function AuthStep({
  onAuthenticated,
  onBeforeAuth,
  onBeforePasswordless,
  passwordlessSentMessage = 'Open it on this device to finish signing up.',
}: AuthStepProps) {
  const dispatch = useDispatch()
  const auth = getAuth()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alreadyRegistered, setAlreadyRegistered] = useState(false)
  const [linkSent, setLinkSent] = useState(false)

  const resetErrors = () => {
    setError(null)
    setAlreadyRegistered(false)
  }

  const handleGoogle = async () => {
    resetErrors()
    if (onBeforeAuth && !onBeforeAuth()) return
    setBusy(true)
    dispatch(setNeedsRegistration(true))
    try {
      await SingUpWithGoogle()
      onAuthenticated()
    } catch (e) {
      dispatch(setNeedsRegistration(false))
      if (
        e &&
        typeof e === 'object' &&
        'code' in e &&
        (e.code === 'auth/popup-closed-by-user' ||
          e.code === 'auth/cancelled-popup-request')
      ) {
        setError('The Google sign-in window was closed. Please try again.')
      } else {
        setError('Could not sign in with Google. Please try another method.')
      }
      setBusy(false)
    }
  }

  const handleEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    resetErrors()
    if (!name.trim() || !email.trim() || !password) {
      setError('Please fill in your name, email, and password.')
      return
    }
    if (onBeforeAuth && !onBeforeAuth()) return
    setBusy(true)
    dispatch(setNeedsRegistration(true))
    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      )
      // Set the display name so the refreshed token carries a `name` claim,
      // which PUT /user reads to materialize the backend user.
      await updateProfile(credential.user, { displayName: name.trim() })
      const token = await credential.user.getIdToken(true)
      dispatch(
        setFirebaseUser({ idpId: credential.user.uid, accessToken: token })
      )
      onAuthenticated()
    } catch (err) {
      dispatch(setNeedsRegistration(false))
      setBusy(false)
      if (err && typeof err === 'object' && 'code' in err) {
        if (err.code === 'auth/email-already-in-use') {
          setAlreadyRegistered(true)
          return
        }
        if (err.code === 'auth/weak-password') {
          setError('Please choose a password with at least 6 characters.')
          return
        }
        if (err.code === 'auth/invalid-email') {
          setError('Please enter a valid email address.')
          return
        }
      }
      setError('Could not create your account. Please try again.')
    }
  }

  // Passwordless reuses the name/email entered above — it just doesn't need a
  // password.
  const handleSendLink = async () => {
    resetErrors()
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and email.')
      return
    }
    if (onBeforePasswordless && !onBeforePasswordless(email.trim(), name.trim())) {
      return
    }
    setBusy(true)
    try {
      await sendSignInLinkToEmail(auth, email.trim(), {
        url: `${window.location.origin}${COMPLETE_SIGN_UP_PATH}`,
        handleCodeInApp: true,
      })
      setLinkSent(true)
    } catch {
      setError('Could not send the sign-in link. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (linkSent) {
    return (
      <div className="rounded-md bg-green-50 p-4 text-center">
        <p className="text-sm font-semibold text-green-800">
          Check your email
        </p>
        <p className="mt-1 text-sm text-green-700">
          We sent a sign-in link to {email}. {passwordlessSentMessage}
        </p>
        <p className="mt-2 text-sm text-green-700">
          Don't see it? Check your spam or junk folder — the link sometimes lands
          there.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <form className="space-y-4" onSubmit={handleEmailPassword}>
        <div>
          <label
            htmlFor="auth-name"
            className="block text-sm font-medium leading-6 text-gray-900">
            Full name
          </label>
          <input
            id="auth-name"
            name="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        <div>
          <label
            htmlFor="auth-email"
            className="block text-sm font-medium leading-6 text-gray-900">
            Email address
          </label>
          <input
            id="auth-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        <div>
          <label
            htmlFor="auth-password"
            className="block text-sm font-medium leading-6 text-gray-900">
            Password
          </label>
          <input
            id="auth-password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {alreadyRegistered && (
          <p className="text-sm text-red-600">
            This email is already registered.{' '}
            <a
              href="/login"
              className="font-semibold text-indigo-600 hover:text-indigo-500">
              Please sign in
            </a>
            .
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          {busy ? <LoadingSpinner /> : 'Create account'}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm font-medium leading-6">
          <span className="bg-white px-6 text-gray-900">Or continue with</span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGoogle}
        disabled={busy}
        className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:ring-transparent">
        <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
            fill="#EA4335"
          />
          <path
            d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
            fill="#4285F4"
          />
          <path
            d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
            fill="#FBBC05"
          />
          <path
            d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
            fill="#34A853"
          />
        </svg>
        <span className="text-sm font-semibold leading-6">Google</span>
      </button>

      <button
        type="button"
        onClick={handleSendLink}
        disabled={busy}
        className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors duration-200">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-5 w-5">
          <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
          <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
        </svg>
        <span className="text-sm font-semibold leading-6">
          Email me a sign-in link
        </span>
      </button>
    </div>
  )
}
